import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { HERO_COLORS } from "./constants";

/**
 * Deep-space particle field. Two layers:
 *   - Distant stars (white-violet, static)
 *   - Nearer floaters (pink, drifting)
 * Rendered as a single Points cloud with custom colors per-vertex.
 */
export default function Particles({ count = 1400 }) {
  const ref = useRef();

  const { positions, colors, sizes } = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const colors    = new Float32Array(count * 3);
    const sizes     = new Float32Array(count);

    const cA = new THREE.Color(HERO_COLORS.purple);
    const cB = new THREE.Color(HERO_COLORS.pink);
    const cC = new THREE.Color(HERO_COLORS.glow);
    const cW = new THREE.Color("#ffffff");

    for (let i = 0; i < count; i++) {
      /* Random point inside a spherical shell (r 4..18) */
      const r = 4 + Math.random() * 14;
      const theta = Math.random() * Math.PI * 2;
      const phi   = Math.acos(2 * Math.random() - 1);
      positions[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);

      const pick = Math.random();
      const c = pick < 0.55 ? cW : pick < 0.78 ? cA : pick < 0.92 ? cC : cB;
      colors[i * 3 + 0] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;

      sizes[i] = Math.random() * 0.06 + 0.02;
    }
    return { positions, colors, sizes };
  }, [count]);

  useFrame((_, delta) => {
    if (ref.current) ref.current.rotation.y += delta * 0.008;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        <bufferAttribute attach="attributes-color"    args={[colors, 3]}    />
        <bufferAttribute attach="attributes-size"     args={[sizes, 1]}     />
      </bufferGeometry>
      <pointsMaterial
        vertexColors
        size={0.055}
        sizeAttenuation
        transparent
        opacity={0.85}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}
